import { useEffect, useMemo, useState } from 'react';
import { Button, Dialog, Input, Select } from '@dreamtree-org/twreact-ui';
import { PackageCheck, RotateCcw } from 'lucide-react';
import { useAppApi } from '@/lib/api';
import { useAppToast } from '@/lib/toast';
import Textarea from './Textarea';
import ProductImage from './ProductImage';
import { currency, dateInput, errorMessage, quantity as qty } from '@/lib/format';
import { DEFAULT_POLICY, expiryMeta, expiryPhrase, previewState } from '@/lib/expiry';

/**
 * STOCK OUT AND REORDER.
 *
 * The rotation this app is built around: a batch nears its date, the shelf is
 * cleared, fresh stock is ordered, and the delivery carries a new expiry date.
 * One dialog, because it is one decision — splitting it into "delete the old"
 * and "add the new" would lose the link between them and the history with it.
 *
 * It has three modes, chosen from the product's own state rather than asked:
 *   • on the shelf          → stock out + reorder (the "Reorder now" half can be turned off)
 *   • stocked out already   → record the delivery (no stock-out half to show)
 */
const REASON_LABELS = {
  near_expiry: 'Pulled before expiry',
  expired: 'Expired on the shelf',
  sold_out: 'Sold out',
  damaged: 'Damaged',
  returned: 'Returned to supplier',
  recalled: 'Recalled',
  transferred: 'Transferred out',
};

const PRESETS = [
  { label: '+1 month', months: 1 },
  { label: '+3 months', months: 3 },
  { label: '+6 months', months: 6 },
  { label: '+1 year', months: 12 },
  { label: '+2 years', months: 24 },
];

function shift({ months }) {
  const d = new Date();
  d.setMonth(d.getMonth() + months);
  return dateInput(d);
}

/** B02403 → B02404, matching what the server would generate. */
function nextBatchNo(previous) {
  const m = /^([A-Za-z-]*)(\d+)$/.exec(String(previous || '').trim());
  if (!m) return '';
  return `${m[1]}${String(Number(m[2]) + 1).padStart(m[2].length, '0')}`;
}

export default function RestockDialog({ product, isOpen, onClose, onSaved, policy = DEFAULT_POLICY, reasons }) {
  const { fetchApi } = useAppApi();
  const toast = useAppToast();

  const [reason, setReason] = useState('near_expiry');
  const [reorder, setReorder] = useState(true);
  const [expiry, setExpiry] = useState('');
  const [batch, setBatch] = useState('');
  const [amount, setAmount] = useState('');
  const [price, setPrice] = useState('');
  const [made, setMade] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');

  // Already stocked out → there is nothing to remove; this is the delivery.
  const awaiting = !!product && product.status === 'disposed' && Number(product.quantity) === 0;

  useEffect(() => {
    if (!isOpen || !product) return;
    // An expired batch defaults to "expired"; one pulled in time defaults to
    // "pulled before expiry" — the reason is usually whatever the state says.
    setReason(product.expiry_state === 'expired' ? 'expired' : 'near_expiry');
    setReorder(true);
    setExpiry('');
    setBatch(nextBatchNo(product.batch_no));
    setAmount(product.quantity != null && !awaiting ? String(Math.round(Number(product.quantity))) : '');
    setPrice('');
    setMade('');
    setNotes('');
    setErr('');
  }, [isOpen, product, awaiting]);

  const reasonOptions = useMemo(
    () => (reasons && reasons.length ? reasons : Object.keys(REASON_LABELS)).map((r) => ({
      value: r,
      label: REASON_LABELS[r] || r,
    })),
    [reasons]
  );

  if (!product) return null;

  const willReorder = awaiting || reorder;
  const outgoingValue = Number(product.quantity || 0) * Number(product.unit_price || 0);

  const previewDays = expiry
    ? Math.round((new Date(`${expiry}T00:00:00`).getTime() - new Date(new Date().toDateString()).getTime()) / 86400000)
    : null;
  const previewMeta = previewDays == null ? null : expiryMeta(previewState(previewDays, policy));

  const save = async () => {
    if (willReorder && !expiry) {
      setErr('Set the new expiry date from the delivery');
      return;
    }
    setSaving(true);
    setErr('');
    try {
      const res = await fetchApi('productRestock', {
        id: product.id,
        reason,
        notes: notes.trim() || undefined,
        ...(willReorder
          ? {
              expiry_date: expiry,
              batch_no: batch.trim() || undefined,
              quantity: amount === '' ? undefined : Number(amount),
              unit_price: price === '' ? undefined : Number(price),
              manufactured_date: made || undefined,
            }
          : {}),
      });
      toast.success(willReorder ? 'Stock rotated' : 'Stocked out', (res && res.message) || product.name);
      onSaved && onSaved((res && res.data) || null);
      onClose();
    } catch (ex) {
      setErr(errorMessage(ex, 'Could not update this batch'));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      isOpen={isOpen}
      onClose={onClose}
      title={awaiting ? 'Record delivery' : 'Stock out & reorder'}
      size="md"
      footer={
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} disabled={saving}>
            Cancel
          </Button>
          <Button
            onClick={save}
            loading={saving}
            leftIcon={willReorder ? <PackageCheck size={16} /> : <RotateCcw size={16} />}
          >
            {awaiting ? 'Record delivery' : willReorder ? 'Stock out & reorder' : 'Stock out only'}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {/* What is leaving the shelf */}
        <div className="flex items-center gap-3 rounded-xl border border-[var(--app-border)] bg-[var(--app-surface-2)] p-3">
          <ProductImage src={product.image_url} name={product.name} className="h-14 w-14 shrink-0" />
          <div className="min-w-0">
            <div className="truncate font-semibold text-[var(--app-text)]">{product.name}</div>
            <div className="truncate text-xs text-[var(--app-muted)]">
              {product.sku}
              {product.batch_no ? ` · batch ${product.batch_no}` : ''}
            </div>
            <div className="mt-0.5 text-xs" style={{ color: expiryMeta(product.expiry_state).fg }}>
              {awaiting ? 'Stocked out — awaiting delivery' : expiryPhrase(product.days_to_expiry)}
            </div>
          </div>
        </div>

        {/* The stock-out half — hidden when there is nothing left to remove */}
        {!awaiting ? (
          <div className="space-y-3 rounded-xl border border-[var(--app-border)] p-3">
            <div className="flex items-center justify-between gap-3">
              <h3 className="text-sm font-bold text-[var(--app-text)]">Going out</h3>
              <span className="nums text-xs text-[var(--app-muted)]">
                {qty(product.quantity, product.unit)} · {currency(outgoingValue)}
              </span>
            </div>
            <Select label="Reason" options={reasonOptions} value={reason} onChange={(v) => setReason(v || 'near_expiry')} />
            <label className="flex cursor-pointer items-start gap-2 text-sm text-[var(--app-text)]">
              <input
                type="checkbox"
                checked={reorder}
                onChange={(e) => setReorder(e.target.checked)}
                className="mt-0.5 h-4 w-4 rounded border-[var(--app-border)] accent-indigo-600"
              />
              <span>
                Reorder now
                <span className="block text-xs text-[var(--app-muted)]">
                  Uncheck to stock out and record the delivery later — it will sit under “Awaiting delivery”.
                </span>
              </span>
            </label>
          </div>
        ) : null}

        {/* The incoming batch */}
        {willReorder ? (
          <div className="space-y-3 rounded-xl border border-[var(--app-border)] p-3">
            <h3 className="text-sm font-bold text-[var(--app-text)]">Coming in</h3>
            <Input
              label="New expiry date"
              type="date"
              value={expiry}
              onChange={(e) => setExpiry(e.target.value)}
              error={err && !expiry ? err : undefined}
              required
            />
            <div className="flex flex-wrap gap-1.5">
              {PRESETS.map((p) => (
                <button
                  key={p.label}
                  type="button"
                  onClick={() => setExpiry(shift(p))}
                  className="rounded-full border border-[var(--app-border)] bg-[var(--app-surface)] px-3 py-1 text-xs font-medium text-[var(--app-text)] transition-colors hover:border-primary-400 hover:text-primary-600"
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              <Input label="New batch number" value={batch} onChange={(e) => setBatch(e.target.value)} placeholder="Auto" />
              <Input
                label={`Quantity received${product.unit ? ` (${product.unit})` : ''}`}
                type="number"
                min="0"
                step="0.01"
                value={amount}
                onChange={(e) => setAmount(e.target.value)}
              />
              <Input label="Manufactured on" type="date" value={made} onChange={(e) => setMade(e.target.value)} />
              <Input
                label="Unit price (₹)"
                type="number"
                min="0"
                step="0.01"
                value={price}
                onChange={(e) => setPrice(e.target.value)}
                placeholder={product.unit_price != null ? String(product.unit_price) : '0.00'}
              />
            </div>
          </div>
        ) : null}

        <Textarea
          label="Notes"
          value={notes}
          onChange={(e) => setNotes(e.target.value)}
          rows={2}
          placeholder="Kept with the batch history — supplier, PO number, anything worth remembering"
        />

        {previewMeta && willReorder ? (
          <div
            className="rounded-xl border px-3 py-2 text-sm font-medium"
            style={{ color: previewMeta.fg, background: previewMeta.bg, borderColor: previewMeta.border }}
          >
            After this: {expiryPhrase(previewDays)} — {previewMeta.blurb}
          </div>
        ) : null}

        {err && expiry ? <p className="text-sm text-error-600">{err}</p> : null}
      </div>
    </Dialog>
  );
}
